import axios from 'axios';

/**
 * Cliente da plataforma operacional (flow-updatecommit), que é um host
 * diferente do backend deste site.
 *
 * Deliberadamente separado de services/api.ts: aquele atende auth, blog e
 * produtos no backend próprio. Repontar os dois para o mesmo lugar quebraria
 * tudo o que não seja lead.
 *
 * Sem instância axios com interceptor de token: este cliente só fala com
 * endpoints públicos e não deve carregar credencial nenhuma para outro host.
 */
/**
 * Sem fallback para localhost em produção — foi exatamente assim que os
 * leads se perderam: a variável faltou no build, o bundle saiu apontando
 * para localhost:3001 e o navegador de cada visitante tentava postar na
 * própria máquina. Silenciosamente, com o site parecendo funcionar.
 *
 * Agora, faltando a variável, o envio falha na hora e com motivo explícito.
 * O `prebuild` (scripts/check-env.js) barra o build antes disso acontecer;
 * esta checagem é a segunda linha de defesa.
 */
const PLATFORM_API_URL =
  process.env.REACT_APP_PLATFORM_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001/api');

/** Campos de rastreio aceitos pelo endpoint. Todos opcionais. */
export interface RastreioLead {
  /** Canal. Lista fechada no servidor; valor fora dela vira 'site'. */
  source?: string;
  /** Página específica que originou o lead. Normalizado em slug no servidor. */
  origin?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_page?: string;
}

export interface SiteLead extends RastreioLead {
  name: string;
  email: string;
  phone: string;
  company?: string;
  budget?: string;
  description: string;
  /** Honeypot: precisa chegar vazio. Preenchido, o backend descarta. */
  website_confirm?: string;
}

const CHAVE = 'uc:rastreio';
const PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

type Guardado = Partial<Record<(typeof PARAMS)[number], string>> & { landing_page?: string };

/**
 * Guarda o rastreio da primeira página visitada.
 *
 * Sem isto a atribuição se perderia no caso mais comum: o anúncio leva a
 * pessoa para a home com ?utm_source=..., ela navega até o formulário e o
 * React Router troca a URL — a query string some antes do envio.
 *
 * sessionStorage e não localStorage: a atribuição vale para esta visita.
 * Guardar entre sessões creditaria a campanha errada semanas depois.
 */
function capturarRastreio(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const achado: Guardado = {};
    PARAMS.forEach((p) => {
      const v = params.get(p);
      if (v) achado[p] = v;
    });
    // Só grava se veio algo, senão uma navegação interna limparia o que
    // a primeira página já tinha capturado.
    if (Object.keys(achado).length === 0) return;
    achado.landing_page = window.location.href;
    window.sessionStorage.setItem(CHAVE, JSON.stringify(achado));
  } catch {
    // sessionStorage indisponível (modo privado, cookies bloqueados).
    // Rastreio é acessório: o lead segue sem ele.
  }
}

function lerRastreio(): Guardado {
  if (typeof window === 'undefined') return {};
  try {
    const cru = window.sessionStorage.getItem(CHAVE);
    return cru ? (JSON.parse(cru) as Guardado) : {};
  } catch {
    return {};
  }
}

// Roda uma vez, no carregamento do bundle — antes de qualquer navegação
// interna do React Router.
capturarRastreio();

/**
 * Envia o lead com o rastreio capturado na chegada.
 * O que for passado em `extra` vence o que está guardado.
 */
export async function enviarLeadDoSite(
  lead: SiteLead,
  extra: RastreioLead = {}
): Promise<void> {
  if (!PLATFORM_API_URL) {
    throw new Error(
      'REACT_APP_PLATFORM_API_URL não foi definida no build. O lead não tem ' +
        'para onde ir. Defina a variável e gere o build novamente.'
    );
  }

  const guardado = lerRastreio();
  await axios.post(
    `${PLATFORM_API_URL}/leads/site`,
    {
      ...lead,
      ...guardado,
      // landing_page da página do envio é mais útil que a de entrada
      landing_page: typeof window !== 'undefined' ? window.location.href : undefined,
      ...extra,
    },
    { headers: { 'Content-Type': 'application/json' }, timeout: 15000 }
  );
}

export default enviarLeadDoSite;
